import { confirmationBadge, confirmationStateOf, type StateBooking } from '@atink/domain';
import { DEPOSIT_STATUS_META, depositAmountLabel, depositHeldAud, deriveDepositStatus } from '@/domain/depositStatus';
import type { Booking, Deposit, Waiver } from '@/domain/types';

/**
 * Shared status chips for the customer card's readiness tier. Every screen that
 * shows a booking's deposit/waiver/confirmation state consumes these, so the
 * same booking never shows different chip colours across screens. Each returns
 * a display label + a semantic pill utility class (styles/index.css).
 */
export interface CardChip {
  label: string;
  cls: string;
}

/**
 * A deposit's human status label, never the raw status value (tv-2na.11.4): the
 * shared DEPOSIT_STATUS_META display label (Sent / Viewed / Paid / Placed / Lapsed /
 * Forfeited / Expired / Link resent), plus the two stored states the display
 * status does not model (a rolled-over or refunded deposit).
 */
export function depositStatusLabel(deposit: Deposit, nowMs: number, booking?: Booking): string {
  if (deposit.status === 'rolled-over') return 'Rolled over';
  if (deposit.status === 'refunded') return 'Refunded';
  if (deposit.status === 'forfeited') return DEPOSIT_STATUS_META.forfeited.label;
  return DEPOSIT_STATUS_META[deriveDepositStatus(deposit, nowMs, booking)].label;
}

export function depositChip(
  booking: Pick<Booking, 'depositWaived'>,
  deposit: Deposit | undefined,
  /** B3: the "Approved by Ops · <name> · <date>" stamp behind a waived deposit. */
  approvalLabel?: string,
): CardChip {
  if (booking.depositWaived) return { label: approvalLabel ?? 'Deposit waived', cls: 'pill-cream' };
  // Part of the deposit in (tv-2na.8.18): quote only the money held.
  if (deposit && (deposit.status === 'paid' || deposit.status === 'pending')) {
    const held = depositHeldAud(deposit);
    if (held > 0 && held < deposit.amountAud) return { label: `Deposit ${depositAmountLabel(deposit)}`, cls: 'pill-gold' };
  }
  if (deposit?.status === 'paid') return { label: 'Deposit paid', cls: 'pill-mint' };
  if (deposit?.status === 'pending') return { label: 'Deposit pending', cls: 'pill-gold' };
  if (deposit) {
    // Past pending/paid, the stored status alone decides the label, so no clock is needed.
    return { label: `Deposit ${depositStatusLabel(deposit, 0).toLowerCase()}`, cls: 'pill-cream' };
  }
  return { label: 'No deposit on file', cls: 'pill-cream' };
}

export function waiverChip(waiver: Waiver | undefined): CardChip {
  if (!waiver) return { label: 'No waiver on file', cls: 'pill-cream' };
  if (waiver.status === 'signed') return { label: 'Waiver signed', cls: 'pill-mint' };
  if (waiver.status === 'sent') return { label: 'Waiver sent', cls: 'pill-cream' };
  if (waiver.status === 'expired') return { label: 'Waiver expired', cls: 'pill-cream' };
  return { label: 'Waiver not sent', cls: 'pill-cream' };
}

/**
 * The confirmation chip, derived from the shared cadence state
 * (@atink/domain `confirmationStateOf`). Settled states show their badge
 * (Confirmed / Confirmed at booking / With Ops); every in-progress state reads
 * "Unconfirmed" so the chip stays a compact readiness signal. `nowIso` only
 * matters for in-progress states; it defaults to the appointment start.
 */
export function confirmChip(
  booking: StateBooking,
  opts?: { overdue?: boolean; nowIso?: string },
): CardChip {
  const state = confirmationStateOf(booking, opts?.nowIso ?? booking.start);
  if (state === 'confirmed' || state === 'confirmed-at-booking' || state === 'escalated') {
    const badge = confirmationBadge(state);
    return { label: badge.label, cls: `pill-${badge.tone}` };
  }
  // Reserved for consumers without a separate urgency pill (e.g. the Ops list);
  // the confirmations worklist carries overdue via its role-zone state pill instead.
  if (opts?.overdue) return { label: 'Unconfirmed', cls: 'pill-blood' };
  return { label: 'Unconfirmed', cls: 'pill-cream' };
}
